import React, { memo, useCallback, useRef, useState, type ReactElement } from "react";

import { ResponsiveAsset } from "@funnel-ui/primitives/ResponsiveAsset";

import "./DualChoiceHero.css";

/* ── Types ─────────────────────────────────────────────── */

export type DualChoiceOption = {
  id: string;
  label: string;
  subLabel?: string;
  imageUrl: string;
};

export type DualChoiceMode = "split" | "toggle";
export type DualChoiceDesktopOrientation = "auto" | "horizontal" | "vertical";

type DualChoiceHeroProps = {
  mode?: DualChoiceMode;
  desktopOrientation?: DualChoiceDesktopOrientation;
  eyebrow?: string;
  headline?: string;
  subhead?: string;
  options: DualChoiceOption[];
  ctaLabel?: string;
  ctaPrefix?: string;
  disclaimer?: string;
  initialActiveId?: string;
  onSelect?: (id: string) => void;
  className?: string;
  testId?: string;
};

/* ── Helpers ───────────────────────────────────────────── */

const ArrowIcon = (): ReactElement => (
  <svg
    className="dch__arrow"
    width="1em"
    height="1em"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M5 12h14" />
    <path d="m12 5 7 7-7 7" />
  </svg>
);

function renderMultiline(text: string): ReactElement[] {
  return text.split("\n").map((line, i, arr) => (
    <span key={i}>
      <span className="dch__headline-line">{line}</span>
      {i < arr.length - 1 ? <br className="dch__headline-break" /> : null}
    </span>
  ));
}

/* ── Split Mode ────────────────────────────────────────── */

const SplitLayout = ({
  desktopOrientation = "auto",
  eyebrow,
  headline,
  options,
  ctaLabel = "Start",
  initialActiveId,
  onSelect,
  className,
  testId,
}: Omit<DualChoiceHeroProps, "mode" | "subhead" | "ctaPrefix" | "disclaimer">): ReactElement => {
  const [hover, setHover] = useState<string | null>(null);

  const handleSelect = useCallback((id: string) => onSelect?.(id), [onSelect]);

  return (
    <div
      data-testid={testId}
      className={`dch dch--split ${className ?? ""}`}
      data-hover={hover ?? "none"}
      data-desktop-orientation={desktopOrientation}
    >
      <div className="dch__header">
        {eyebrow && <span className="dch__eyebrow dch__eyebrow--pill">{eyebrow}</span>}
        {headline && <h1 className="dch__headline">{renderMultiline(headline)}</h1>}
      </div>

      <div className="dch__halves" role="group" aria-label={eyebrow ?? "Style"}>
        {options.slice(0, 2).map((opt, i) => (
          <button
            key={opt.id}
            type="button"
            aria-label={`${opt.label} — start`}
            aria-pressed={initialActiveId === opt.id}
            data-idx={i}
            className="dch__side"
            onPointerEnter={() => setHover(String(i))}
            onPointerLeave={() => setHover(null)}
            onFocus={() => setHover(String(i))}
            onBlur={() => setHover(null)}
            onClick={() => handleSelect(opt.id)}
          >
            <ResponsiveAsset
              src={opt.imageUrl}
              alt=""
              draggable={false}
              className="dch__side-img"
              loading="eager"
            />
            <div className="dch__side-grad" aria-hidden="true" />
            <div className="dch__side-label">
              {opt.subLabel && <div className="dch__eyebrow">{opt.subLabel}</div>}
              <div className="dch__side-name">{opt.label}</div>
            </div>
            <div className="dch__side-cta" aria-hidden="true">
              {ctaLabel} <ArrowIcon />
            </div>
          </button>
        ))}
        <div className="dch__vs" aria-hidden="true">
          VS
        </div>
      </div>
    </div>
  );
};

/* ── Toggle Mode ───────────────────────────────────────── */

const ToggleLayout = ({
  eyebrow,
  headline,
  subhead,
  options,
  ctaPrefix = "Start with ",
  disclaimer,
  initialActiveId,
  onSelect,
  className,
  testId,
}: Omit<DualChoiceHeroProps, "mode" | "desktopOrientation" | "ctaLabel">): ReactElement => {
  const [activeIdx, setActiveIdx] = useState<number>(() => {
    if (initialActiveId == null) return 0;
    const i = options.findIndex((o) => o.id === initialActiveId);
    return i >= 0 ? i : 0;
  });

  const handleToggleCta = useCallback(() => {
    const id = options[activeIdx]?.id;
    if (id) onSelect?.(id);
  }, [activeIdx, options, onSelect]);

  const toggleBtnRefs = useRef<(HTMLButtonElement | null)[]>([]);

  const handleToggleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      const count = Math.min(options.length, 2);
      if (count < 2) return;
      let next: number | null = null;
      if (e.key === "ArrowRight" || e.key === "ArrowDown") {
        e.preventDefault();
        next = (activeIdx + 1) % count;
      } else if (e.key === "ArrowLeft" || e.key === "ArrowUp") {
        e.preventDefault();
        next = (activeIdx - 1 + count) % count;
      }
      if (next != null) {
        setActiveIdx(next);
        toggleBtnRefs.current[next]?.focus();
      }
    },
    [activeIdx, options.length],
  );

  const active = options[activeIdx];

  return (
    <div
      data-testid={testId}
      className={`dch dch--toggle ${className ?? ""}`}
      data-active-idx={activeIdx}
    >
      <div className="dch__hero-stack" aria-hidden="true">
        {options.slice(0, 2).map((opt, i) => (
          <ResponsiveAsset
            key={opt.id}
            src={opt.imageUrl}
            alt=""
            draggable={false}
            className="dch__hero-img"
            data-idx={i}
            loading="eager"
          />
        ))}
        <div className="dch__grad-top" />
        <div className="dch__grad-bottom" />
      </div>

      <div className="dch__header">
        {eyebrow && <span className="dch__eyebrow">{eyebrow}</span>}
        {headline && <h1 className="dch__headline">{renderMultiline(headline)}</h1>}
        {subhead && <p className="dch__subhead">{subhead}</p>}
      </div>

      <div className="dch__footer">
        <div
          className="dch__toggle"
          role="radiogroup"
          aria-label={eyebrow ?? "Style"}
          onKeyDown={handleToggleKeyDown}
        >
          <div className="dch__toggle-indicator" aria-hidden="true" />
          {options.slice(0, 2).map((opt, i) => (
            <button
              key={opt.id}
              ref={(el) => {
                toggleBtnRefs.current[i] = el;
              }}
              type="button"
              role="radio"
              aria-checked={activeIdx === i}
              tabIndex={activeIdx === i ? 0 : -1}
              className="dch__toggle-btn"
              data-active={activeIdx === i}
              onClick={() => setActiveIdx(i)}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <button type="button" className="dch__cta" onClick={handleToggleCta}>
          <span className="dch__cta-label">
            {ctaPrefix}
            <span>{active?.label}</span>
          </span>
          <ArrowIcon />
        </button>
        {disclaimer && <p className="dch__disclaimer">{disclaimer}</p>}
      </div>
    </div>
  );
};

/* ── Main Component ────────────────────────────────────── */

const DualChoiceHeroImpl = (props: DualChoiceHeroProps): ReactElement | null => {
  const { mode = "split", options } = props;

  if (!options || options.length < 2) {
    return (
      <div className={`dch ${props.className ?? ""}`}>
        <div style={{ padding: 24, color: "white", opacity: 0.7 }}>
          DualChoiceHero needs at least 2 options.
        </div>
      </div>
    );
  }

  if (mode === "toggle") {
    return <ToggleLayout {...props} />;
  }

  return <SplitLayout {...props} />;
};

export const DualChoiceHero = memo(DualChoiceHeroImpl);

export default DualChoiceHero;
